import ClearIcon from '@mui/icons-material/Clear'
import Tooltip from '@mui/material/Tooltip'
import { SearchIcon } from 'components/icons'
import InputMobileComponent from 'components/input-mobile'
import RequiredAsteriskComponent from 'components/required-asterisk'
import useClickOutside from 'hooks/useClickOutside'
import { useRef, useState } from 'react'
import { ReactNode } from 'react'
import './index.css'

interface OptionType {
  value: string
  label: string | ReactNode
  className?: string
  searchText?: string
}

const CustomArrowIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M2.90245 4.62445L1.57495 5.95945L8.99995 13.377L16.4249 5.95195L15.0974 4.62445L8.99995 10.722L2.90245 4.62445Z'
      fill='#A3A3A3'
    />
  </svg>
)

const SelectMobileComponent = ({
  isDisabled,
  label,
  placeholder = 'Select an option',
  tooltip,
  options,
  value,
  onChange,
  isError,
  data_testid,
  isReadOnly,
  isRequired,
  searchable = false,
}: {
  isDisabled?: boolean
  label?: string
  placeholder?: string
  tooltip?: string
  options: Array<string | OptionType>
  value?: string
  onChange: (value: string) => void
  isError?: boolean
  data_testid?: string
  isReadOnly?: boolean
  isRequired?: boolean
  searchable?: boolean
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selectedValue = value || ''

  useClickOutside(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false)
      setSearchTerm('')
    }
  })

  const getOptionLabel = (option: string | OptionType): string | ReactNode => {
    return typeof option === 'string' ? option : option.label
  }

  const getOptionValue = (option: string | OptionType): string => {
    return typeof option === 'string' ? option : option.value
  }

  const handleSelectChange = (option: string | OptionType) => {
    const value = getOptionValue(option)
    onChange(value)
    setIsOpen(false)
    setSearchTerm('')
  }

  const filteredOptions = options.filter((option) => {
    if (!searchTerm) return true

    // Check if the option has a searchText property
    if (typeof option === 'object' && 'searchText' in option && option.searchText) {
      return option.searchText.toLowerCase().includes(searchTerm.toLowerCase())
    }

    // Fallback to checking the label if no searchText is provided
    const label = getOptionLabel(option)
    if (typeof label === 'string') {
      return label.toLowerCase().includes(searchTerm.toLowerCase())
    }
    return false
  })

  const selectedOption = options.find((option) => getOptionValue(option) === selectedValue)
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : placeholder

  return (
    <div className='relative' ref={dropdownRef}>
      {label && (
        <label className='text-gray-400 text-sm font-medium'>
          {label}
          {isRequired && <RequiredAsteriskComponent />}
        </label>
      )}
      <div
        className={`relative`}
        style={{
          boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1)',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          marginTop: '4px',
        }}
      >
        <div
          className={`flex justify-between items-center p-2 ${
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          } ${isError ? 'border-red-500' : 'border-gray-300'}  text-gray-900
          ${isReadOnly ? 'text-disable opacity-65 cursor-default' : ''}`}
          onClick={() => !isDisabled && !isReadOnly && setIsOpen(!isOpen)}
          data-testid={data_testid}
        >
          <span>{displayValue}</span>
          {options.length > 0 && <CustomArrowIcon />}
        </div>
        {isOpen && (
          <div
            className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto'
            style={{
              maxHeight: '240px',
              position: 'fixed',
              width: dropdownRef.current?.offsetWidth,
              left: dropdownRef.current?.getBoundingClientRect().left,
              top: dropdownRef.current
                ? (() => {
                    const rect = dropdownRef.current.getBoundingClientRect()
                    const spaceBelow = window.innerHeight - rect.bottom - 10
                    const dropdownHeight = Math.min(240, filteredOptions.length * 40 + 60) // 40px per item + search box

                    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
                      return `${rect.top - dropdownHeight}px`
                    }
                    return `${rect.bottom + 5}px`
                  })()
                : 0,
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            {searchable && (
              <div className='sticky top-0 bg-white p-2 border-b'>
                <div onClick={(e) => e.stopPropagation()}>
                  <InputMobileComponent
                    type='text'
                    placeholder='Search...'
                    data_testid='search-input'
                    value={searchTerm ?? ''}
                    onChange={setSearchTerm}
                    rightElement={searchTerm ? <ClearIcon onClick={() => setSearchTerm('')} /> : <SearchIcon />}
                  />
                </div>
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optionValue = getOptionValue(option)
                const optionLabel = getOptionLabel(option)
                return (
                  <div
                    key={optionValue}
                    className={`p-2.5 cursor-pointer hover:bg-gray-100 ${
                      optionValue === selectedValue ? 'bg-gray-200' : ''
                    }`}
                    onClick={() => handleSelectChange(option)}
                  >
                    {optionLabel}
                  </div>
                )
              })
            ) : (
              <div className='p-2.5 text-gray-500 text-center'>No options found</div>
            )}
          </div>
        )}
      </div>
      {tooltip && (
        <Tooltip title={tooltip} followCursor disableHoverListener>
          <div className='absolute -top-4 left-0 bg-gray-700 text-white text-xs p-2 rounded-lg'>{tooltip}</div>
        </Tooltip>
      )}
    </div>
  )
}

export default SelectMobileComponent
